import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  approveReviewItemL1,
  approveReviewItemL2,
  bulkReviewItemsAction,
  fetchReviewItems,
  getJobStatus,
  rejectReviewItem,
} from "../../services/api";
import { useDeviceDetection } from "../../hooks/useDeviceDetection";
import { useV2Theme } from "../theme/ThemeProvider";
import {
  countUnseenBaselineRejections,
  markBaselineSubmissionsSeen,
} from "../utils/baselineApprovalSubmitterSeen";
import ApprovalItemDetailView from "../components/ApprovalItemDetailView";
import { useMobileChrome } from "../mobile/MobileChromeContext";

const PENDING_STATES = new Set(["pending_l1", "pending_l2", "changes_requested"]);

function entityIdFromReviewItem(item) {
  if (String(item?.item_kind || "") !== "entity") return null;
  return (
    String(item?.source_record_id || item?.proposed_payload?.id || "").trim() || null
  );
}

const REJECT_REASON_LABELS = {
  not_suitable: "Not right for the map",
  duplicate: "Already on the map",
  incomplete: "Incomplete information",
  other: "Other",
};

function stateLabel(state) {
  const s = String(state || "");
  if (s === "pending_l1") return "Awaiting approval";
  if (s === "pending_l2") return "Awaiting second approval";
  if (s === "applied") return "On your map";
  if (s === "rejected") return "Rejected";
  if (s === "changes_requested") return "Changes requested";
  return s || "Unknown";
}

function rejectionSummary(item) {
  const code = String(item?.rejection_reason_code || "");
  const label = REJECT_REASON_LABELS[code] || code || "Rejected";
  const notes = String(item?.rejection_notes || "").trim();
  return { label, notes };
}

function parseJobResult(raw) {
  if (!raw) return {};
  if (typeof raw === "object") return raw;
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      return {};
    }
  }
  return {};
}

function canDrillIntoReviewItem(item) {
  const kind = String(item?.item_kind || "");
  if (kind === "entity") return Boolean(entityIdFromReviewItem(item));
  return false;
}

function ReviewItemCard({
  item,
  isInlineLight,
  tokens,
  onActionDone,
  selectable,
  selected,
  onToggleSelect,
  readOnly,
  canAct = true,
  onOpenDetail = null,
}) {
  const [note, setNote] = useState("");
  const [reasonCode, setReasonCode] = useState("not_suitable");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  const summary = item?.summary || {};
  const title = summary.title || item?.proposed_payload?.name || "Submission";
  const subtitle = summary.subtitle || "";
  const pending = PENDING_STATES.has(item?.state);
  const rejected = String(item?.state || "") === "rejected";
  const applied = String(item?.state || "") === "applied";
  const actionable = pending && canAct !== false && item?.can_act !== false;
  const drillable = canDrillIntoReviewItem(item) && typeof onOpenDetail === "function";
  const { label: rejectLabel, notes: rejectNotes } = rejectionSummary(item);

  const shell = isInlineLight
    ? "rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
    : "rounded-xl border border-slate-700 bg-slate-900/50 p-4";

  const runApprove = async () => {
    setBusy("approve");
    setError("");
    try {
      if (item.state === "pending_l2") {
        await approveReviewItemL2(item.id, note);
      } else {
        await approveReviewItemL1(item.id, note);
      }
      onActionDone?.();
    } catch (e) {
      setError(e?.message || "Approve failed.");
    } finally {
      setBusy("");

    }

  };



  const runReject = async () => {

    setBusy("reject");

    setError("");

    try {

      await rejectReviewItem(item.id, reasonCode, note);

      onActionDone?.();

    } catch (e) {

      setError(e?.message || "Reject failed.");

    } finally {

      setBusy("");

    }

  };



  return (

    <article className={shell}>

      <div className="flex items-start gap-3">

        {selectable ? (

          <input

            type="checkbox"

            className="mt-1 h-4 w-4 rounded border-slate-300"

            checked={Boolean(selected)}

            disabled={!actionable}

            onChange={() => onToggleSelect?.(item.id)}

            aria-label={`Select ${title}`}

          />

        ) : null}

        <div className="flex flex-1 items-start justify-between gap-3 min-w-0">

          <div className="min-w-0">

            {drillable ? (
              <button
                type="button"
                onClick={() => onOpenDetail(item)}
                className={
                  isInlineLight
                    ? "text-sm font-semibold text-sky-800 text-left hover:text-sky-950 hover:underline underline-offset-2"
                    : "text-sm font-semibold text-sky-300 text-left hover:text-sky-100 hover:underline underline-offset-2"
                }
              >
                {title}
              </button>
            ) : (
              <p
                className={
                  isInlineLight
                    ? "text-sm font-semibold text-slate-900"
                    : "text-sm font-semibold text-slate-100"
                }
              >
                {title}
              </p>
            )}

            {subtitle ? (

              <p className={isInlineLight ? "text-xs text-slate-600 mt-1" : "text-xs text-slate-400 mt-1"}>

                {subtitle}

              </p>

            ) : null}

            <p className={isInlineLight ? "text-xs text-slate-500 mt-1" : "text-xs text-slate-500 mt-1"}>

              {item.entity_type ? `${item.entity_type} · ` : ""}

              {stateLabel(item.state)}

              {item.l2_required ? " · second approval required" : ""}

            </p>

            {rejected ? (

              <div className="mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900">

                <p className="font-medium">Rejected: {rejectLabel}</p>

                {rejectNotes ? <p className="mt-1 text-red-800">{rejectNotes}</p> : null}

                <p className="mt-1 text-red-700">

                  The item stays on your map as Rejected. Fix it in Your map and submit again when

                  ready.

                </p>

              </div>

            ) : null}

            {applied ? (

              <p className="mt-2 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">

                Added to your map.

              </p>

            ) : null}

          </div>

          <span

            className={

              pending

                ? "shrink-0 text-[10px] uppercase font-semibold px-2 py-0.5 rounded border border-amber-300 bg-amber-50 text-amber-900"

                : rejected

                  ? "shrink-0 text-[10px] uppercase font-semibold px-2 py-0.5 rounded border border-red-200 bg-red-50 text-red-800"

                  : applied

                    ? "shrink-0 text-[10px] uppercase font-semibold px-2 py-0.5 rounded border border-emerald-200 bg-emerald-50 text-emerald-900"

                    : "shrink-0 text-[10px] uppercase font-semibold px-2 py-0.5 rounded border border-slate-200 bg-slate-50 text-slate-600"

            }

          >

            {item.state}

          </span>

        </div>

      </div>



      {!actionable && pending && item?.can_act === false ? (
        <p className="mt-2 text-xs text-slate-600 pl-7">
          You cannot approve this item — it is assigned to another approver group, or you
          submitted it and your policy blocks self-approval.
        </p>
      ) : null}

      {actionable && !readOnly ? (

        <div className="mt-4 space-y-3 pl-7">

          <textarea

            value={note}

            disabled={Boolean(busy)}

            onChange={(e) => setNote(e.target.value)}

            rows={2}

            placeholder="Comment (optional for approve, shown in audit trail)"

            className={

              isInlineLight

                ? "w-full px-3 py-2 rounded-lg border border-slate-300 text-sm"

                : "w-full px-3 py-2 rounded-lg border border-slate-600 bg-slate-800 text-sm text-slate-100"

            }

          />

          <div className="flex flex-wrap items-center gap-2">

            <label className={isInlineLight ? "text-xs text-slate-600" : "text-xs text-slate-400"}>

              Reject reason:

            </label>

            <select

              value={reasonCode}

              disabled={Boolean(busy)}

              onChange={(e) => setReasonCode(e.target.value)}

              className={

                isInlineLight

                  ? "text-xs px-2 py-1 rounded border border-slate-300"

                  : "text-xs px-2 py-1 rounded border border-slate-600 bg-slate-800"

              }

            >

              <option value="not_suitable">Not right for the map</option>

              <option value="duplicate">Already on the map</option>

              <option value="incomplete">Incomplete information</option>

              <option value="other">Other</option>

            </select>

          </div>

          <div className="flex flex-wrap gap-2">

            <button

              type="button"

              disabled={Boolean(busy)}

              onClick={() => void runApprove()}

              className={tokens.button.success}

            >

              {busy === "approve"

                ? "Approving…"

                : item.state === "pending_l2"

                  ? "Approve (second review)"

                  : "Add to map"}

            </button>

            <button

              type="button"

              disabled={Boolean(busy)}

              onClick={() => void runReject()}

              className={tokens.button.secondary}

            >

              {busy === "reject" ? "Rejecting…" : "Reject"}

            </button>

          </div>

        </div>

      ) : null}



      {error ? <p className="text-xs text-red-600 mt-2 pl-7">{error}</p> : null}

    </article>

  );

}



export default function V2BaselineApprovalsPage() {

  const { tokens } = useV2Theme();
  const { isMobile } = useDeviceDetection();
  const { setChrome } = useMobileChrome();

  const isInlineLight = true;

  const [filter, setFilter] = useState("pending");

  const [items, setItems] = useState([]);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState("");

  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const [bulkNote, setBulkNote] = useState("");

  const [bulkReasonCode, setBulkReasonCode] = useState("not_suitable");

  const [bulkBusy, setBulkBusy] = useState("");

  const [bulkMessage, setBulkMessage] = useState("");
  const [bulkJob, setBulkJob] = useState(null);
  const [unseenRejectionCount, setUnseenRejectionCount] = useState(0);
  const [selectedItem, setSelectedItem] = useState(null);

  const refreshUnseenRejections = useCallback(async () => {
    try {
      const rejected = await fetchReviewItems({
        state: "rejected",
        kind: "entity",
        submitted_by: "me",
        gallery_only: true,
        page_size: 100,
      });
      setUnseenRejectionCount(countUnseenBaselineRejections(rejected?.data || []));
    } catch {
      setUnseenRejectionCount(0);
    }
  }, []);

  const loadItems = useCallback(async () => {

    setLoading(true);

    setError("");

    try {

      if (filter === "pending") {

        const [l1Entity, l2Entity] = await Promise.all([
          fetchReviewItems({ state: "pending_l1", kind: "entity", page_size: 100 }),
          fetchReviewItems({ state: "pending_l2", kind: "entity", page_size: 100 }),
        ]);

        const merged = new Map();
        for (const row of [
          ...(l1Entity?.data || []),
          ...(l2Entity?.data || []),
        ]) {
          if (row?.id) merged.set(row.id, row);
        }
        setItems([...merged.values()]);

      } else if (filter === "updates") {

        const [rejected, applied] = await Promise.all([

          fetchReviewItems({

            state: "rejected",

            kind: "entity",

            submitted_by: "me",

            gallery_only: true,

            page_size: 100,

          }),

          fetchReviewItems({

            state: "applied",

            kind: "entity",

            submitted_by: "me",

            gallery_only: true,

            page_size: 50,

          }),

        ]);

        const merged = [...(rejected?.data || []), ...(applied?.data || [])];

        merged.sort((a, b) => {

          const at = new Date(a?.updated_at || a?.rejected_at || 0).getTime();

          const bt = new Date(b?.updated_at || b?.rejected_at || 0).getTime();

          return bt - at;

        });

        setItems(merged);

        markBaselineSubmissionsSeen(merged.map((it) => it.id));
        setUnseenRejectionCount(0);
        window.dispatchEvent(new CustomEvent("baselineApprovalQueueChanged"));

      } else {

        const [applied, rejected] = await Promise.all([

          fetchReviewItems({ state: "applied", kind: "entity", page_size: 50 }),

          fetchReviewItems({ state: "rejected", kind: "entity", page_size: 50 }),

        ]);

        setItems([...(applied?.data || []), ...(rejected?.data || [])]);

      }

    } catch (e) {

      setError(e?.message || "Could not load approval queue.");

      setItems([]);

    } finally {

      setLoading(false);

    }

  }, [filter]);



  useEffect(() => {
    void loadItems();
    void refreshUnseenRejections();
  }, [loadItems, refreshUnseenRejections]);



  useEffect(() => {
    setSelectedIds(new Set());
    setSelectedItem(null);
  }, [filter]);



  const notifyApprovalQueueChanged = useCallback(() => {

    window.dispatchEvent(new CustomEvent("baselineApprovalQueueChanged"));

  }, []);



  const handleActionDone = useCallback(() => {
    void loadItems();
    void refreshUnseenRejections();
    notifyApprovalQueueChanged();
  }, [loadItems, refreshUnseenRejections, notifyApprovalQueueChanged]);

  useEffect(() => {
    if (!bulkJob?.id) return undefined;
    let cancelled = false;
    let settled = false;
    const finishFromJob = (job) => {
      if (settled) return true;
      const status = String(job?.status || "").toLowerCase();
      if (status === "completed") {
        settled = true;
        const result = parseJobResult(job?.result);
        const action = bulkJob.action || result.action || "approve";
        const ok = Number(result?.succeeded) || 0;
        const fail = Number(result?.failed) || 0;
        const firstError = String(result?.first_error || "").trim();
        setBulkMessage(
          `${action === "approve" ? "Approved" : "Rejected"} ${ok} item${ok === 1 ? "" : "s"}` +
            (fail > 0 ? `; ${fail} failed` : "") +
            (fail > 0 && firstError ? ` — ${firstError}` : "")
        );
        setBulkJob(null);
        handleActionDone();
        return true;
      }
      if (status === "failed" || status === "cancelled") {
        settled = true;
        setError(job?.error_message || job?.message || "Bulk action failed.");
        setBulkJob(null);
        handleActionDone();
        return true;
      }
      return false;
    };
    const poll = async () => {
      try {
        const job = await getJobStatus(bulkJob.id);
        if (!cancelled) finishFromJob(job);
      } catch (e) {
        if (!cancelled) {
          const status = Number(e?.response?.status || 0);
          if (status === 404 || status === 403) {
            setError(e?.message || "Could not follow bulk action progress.");
            setBulkJob(null);
          }
        }
      }
    };
    void poll();
    const timer = window.setInterval(poll, 1500);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [bulkJob, handleActionDone]);



  const pendingItems = useMemo(
    () =>
      items.filter(
        (it) => PENDING_STATES.has(it.state) && it.can_act !== false
      ),
    [items]
  );



  const pendingCount = pendingItems.length;



  const selectedPendingItems = useMemo(

    () => pendingItems.filter((it) => selectedIds.has(it.id)),

    [pendingItems, selectedIds]

  );



  const toggleSelect = useCallback((id) => {

    setSelectedIds((prev) => {

      const next = new Set(prev);

      if (next.has(id)) next.delete(id);

      else next.add(id);

      return next;

    });

  }, []);



  const actionablePendingItems = useMemo(
    () => pendingItems.filter((it) => it.can_act !== false),
    [pendingItems]
  );

  const selectAllPending = useCallback(() => {
    setSelectedIds(new Set(actionablePendingItems.map((it) => it.id)));
  }, [actionablePendingItems]);



  const clearSelection = useCallback(() => {

    setSelectedIds(new Set());

  }, []);



  const runBulkAction = async (action) => {
    const ids = selectedPendingItems.map((it) => it.id);
    if (!ids.length) return;
    setBulkBusy(action);
    setBulkMessage("");
    setError("");
    try {
      const result = await bulkReviewItemsAction({
        itemIds: ids,
        action,
        note: bulkNote,
        reasonCode: bulkReasonCode,
      });
      clearSelection();
      if (result?.job_id) {
        setBulkJob({
          id: result.job_id,
          action,
          count: ids.length,
        });
        setBulkMessage(
          action === "approve"
            ? `Adding ${ids.length} item${ids.length === 1 ? "" : "s"} to your map in the background. You can keep working.`
            : `Rejecting ${ids.length} item${ids.length === 1 ? "" : "s"} in the background. You can keep working.`
        );
        return;
      }
      const ok = Number(result?.succeeded) || 0;
      const fail = Number(result?.failed) || 0;
      const firstError = String(result?.first_error || "").trim();
      setBulkMessage(
        `${action === "approve" ? "Approved" : "Rejected"} ${ok} item${ok === 1 ? "" : "s"}` +
          (fail > 0 ? `; ${fail} failed` : "") +
          (fail > 0 && firstError ? ` — ${firstError}` : "")
      );
      handleActionDone();
    } catch (e) {
      setError(e?.message || "Bulk action failed.");
    } finally {
      setBulkBusy("");
    }
  };



  const showBulkBar = filter === "pending" && selectedPendingItems.length > 0 && !selectedItem;

  const openItemDetail = useCallback((item) => {
    setSelectedItem(item);
  }, []);

  const handleDetailActionDone = useCallback(() => {
    setSelectedItem(null);
    handleActionDone();
  }, [handleActionDone]);

  const closeItemDetail = useCallback(() => setSelectedItem(null), []);

  // Persistent chrome: root keeps logo+tabs; detail keeps Back+tabs.
  useEffect(() => {
    if (!isMobile) return undefined;
    if (selectedItem) {
      const title =
        selectedItem?.summary?.title ||
        selectedItem?.proposed_payload?.name ||
        "Submission";
      setChrome({
        chromeMode: "tabs",
        contextTitle: title,
        contextSubtitle: "",
        onBack: closeItemDetail,
        backLabel: "Approvals",
        headerTrailing: null,
        actionTray: null,
        hideShellHeader: false,
      });
      return undefined;
    }
    setChrome({
      chromeMode: "tabs",
      contextTitle: "Approvals",
      contextSubtitle: "",
      onBack: null,
      backLabel: "",
      headerTrailing: null,
      actionTray: null,
      hideShellHeader: false,
    });
    return undefined;
  }, [closeItemDetail, isMobile, selectedItem, setChrome]);

  if (selectedItem) {
    return (
      <section
        className={
          isMobile ? "flex min-h-0 flex-1 flex-col overflow-hidden" : undefined
        }
      >
        <div
          className={
            isMobile
              ? "min-h-0 flex-1 overflow-y-auto overscroll-y-contain"
              : undefined
          }
        >
          <ApprovalItemDetailView
            item={selectedItem}
            tokens={tokens}
            isInlineLight={isInlineLight}
            onBack={closeItemDetail}
            onActionDone={handleDetailActionDone}
            readOnly={Boolean(bulkBusy)}
            hideInlineBack={isMobile}
          />
        </div>
      </section>
    );
  }

  return (
    <section
      className={
        isMobile ? "flex min-h-0 flex-1 flex-col overflow-hidden" : undefined
      }
    >
      <div
        className={
          isMobile
            ? "min-h-0 flex-1 space-y-5 overflow-y-auto overscroll-y-contain"
            : "space-y-5"
        }
      >
      <div>
        <h2 className="hidden text-2xl font-semibold md:block">Approvals</h2>

        <p className={`${tokens.surface.mutedText} mt-1`}>

          Review baseline entities routed to your approver group. Approve to
          publish, or reject with a comment.

        </p>

      </div>



      <div className="flex flex-wrap items-center gap-2">

        <button

          type="button"

          onClick={() => setFilter("pending")}

          className={

            filter === "pending"

              ? tokens.button.chipActive

              : `${tokens.button.chip} ${tokens.button.chipIdle}`

          }

        >

          Pending ({filter === "pending" && !loading ? pendingCount : "…"})

        </button>

        <button

          type="button"

          onClick={() => setFilter("updates")}

          className={

            filter === "updates"

              ? tokens.button.chipActive

              : `${tokens.button.chip} ${tokens.button.chipIdle}`

          }

        >

          My updates

          {unseenRejectionCount > 0 && filter !== "updates" ? (

            <span className="ml-1.5 inline-flex min-w-[1.125rem] items-center justify-center rounded-full bg-red-500 px-1.5 text-[10px] font-bold text-white">

              {unseenRejectionCount > 99 ? "99+" : unseenRejectionCount}

            </span>

          ) : null}

        </button>

        <button

          type="button"

          onClick={() => setFilter("done")}

          className={

            filter === "done"

              ? tokens.button.chipActive

              : `${tokens.button.chip} ${tokens.button.chipIdle}`

          }

        >

          All completed

        </button>

        <button

          type="button"

          onClick={() => void loadItems()}

          disabled={loading}

          className={`${tokens.button.secondary} disabled:opacity-50`}

        >

          {loading ? "Refreshing…" : "Refresh"}

        </button>

      </div>



      {filter === "pending" && !loading && pendingCount > 0 ? (

        <div className="flex flex-wrap items-center gap-3 text-sm">

          <label className="inline-flex items-center gap-2 cursor-pointer">

            <input

              type="checkbox"

              className="h-4 w-4 rounded border-slate-300"

              checked={

                pendingItems.length > 0 && selectedIds.size === pendingItems.length

              }

              onChange={(e) => {

                if (e.target.checked) selectAllPending();

                else clearSelection();

              }}

            />

            Select all ({pendingCount})

          </label>

          {selectedIds.size > 0 ? (

            <button type="button" className="text-slate-600 underline" onClick={clearSelection}>

              Clear selection

            </button>

          ) : null}

        </div>

      ) : null}



      {showBulkBar ? (

        <div className={`${tokens.surface.card} p-4 space-y-3 border-amber-200`}>

          <p className="text-sm font-medium text-slate-900">

            Bulk action — {selectedPendingItems.length} selected

          </p>

          <textarea

            value={bulkNote}

            disabled={Boolean(bulkBusy)}

            onChange={(e) => setBulkNote(e.target.value)}

            rows={2}

            placeholder="Shared comment for all selected items (optional for approve)"

            className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm"

          />

          <div className="flex flex-wrap items-center gap-2">

            <label className="text-xs text-slate-600">Reject reason (for bulk reject):</label>

            <select

              value={bulkReasonCode}

              disabled={Boolean(bulkBusy)}

              onChange={(e) => setBulkReasonCode(e.target.value)}

              className="text-xs px-2 py-1 rounded border border-slate-300"

            >

              <option value="not_suitable">Not right for the map</option>

              <option value="duplicate">Already on the map</option>

              <option value="incomplete">Incomplete information</option>

              <option value="other">Other</option>

            </select>

          </div>

          <div className="flex flex-wrap gap-2">

            <button

              type="button"

              disabled={Boolean(bulkBusy)}

              onClick={() => void runBulkAction("approve")}

              className={tokens.button.success}

            >

              {bulkBusy === "approve"

                ? "Approving…"

                : `Add ${selectedPendingItems.length} to map`}

            </button>

            <button

              type="button"

              disabled={Boolean(bulkBusy)}

              onClick={() => void runBulkAction("reject")}

              className={tokens.button.secondary}

            >

              {bulkBusy === "reject" ? "Rejecting…" : `Reject ${selectedPendingItems.length}`}

            </button>

          </div>

        </div>

      ) : null}



      {bulkMessage ? (
        <p
          className={
            bulkJob
              ? "text-sm text-sky-800"
              : bulkMessage.includes("failed")
                ? "text-sm text-amber-900"
                : "text-sm text-emerald-700"
          }
        >
          {bulkMessage}
        </p>
      ) : null}

      {error ? <p className="text-sm text-red-600">{error}</p> : null}



      {loading ? (

        <p className={tokens.surface.mutedText}>Loading approval queue…</p>

      ) : items.length === 0 ? (

        <div className={`${tokens.surface.card} p-6`}>

          <p className={tokens.surface.mutedText}>

            {filter === "pending"

              ? "Nothing waiting for approval."

              : filter === "updates"

                ? "No updates on items you submitted."

                : "No completed approvals yet."}

          </p>

        </div>

      ) : (

        <ul className="space-y-3">

          {items.map((item) => (

            <li key={item.id}>

              <ReviewItemCard

                item={item}

                isInlineLight={isInlineLight}

                tokens={tokens}

                onActionDone={handleActionDone}

                onOpenDetail={openItemDetail}

                selectable={filter === "pending"}

                selected={selectedIds.has(item.id)}

                onToggleSelect={toggleSelect}

                readOnly={Boolean(bulkBusy)}

              />

            </li>

          ))}

        </ul>

      )}



      {filter === "pending" && !loading && pendingCount > 0 ? (

        <p className={`text-xs ${tokens.surface.subtleText}`}>

          {pendingCount} item{pendingCount === 1 ? "" : "s"} awaiting your decision. Select multiple

          items to approve or reject in bulk.

        </p>

      ) : null}



      {filter === "updates" && !loading ? (

        <p className={`text-xs ${tokens.surface.subtleText}`}>

          When an approver rejects your submission, the item stays on your map as{" "}

          <span className="font-medium">Rejected</span>. Fix it in Your map and submit again.

        </p>

      ) : null}
      </div>
    </section>
  );
}


